/**
 * Serialization utilities for TiptapTemplateEditor
 *
 * Converts between template markup format and tiptap JSON content:
 * - Template format: "Hello <highlight>world</highlight> @termsLink"
 * - Tiptap format: JSON with nodes and marks
 */

import type { JSONContent } from '@tiptap/core';
import type { Editor } from '@tiptap/react';

// Regex patterns for parsing template markup
// Using [^\s@<>]+ and [^\s<>/]+ to allow special characters in names (e.g., apostrophes)
// Must stay in sync with templateTagParser.ts
const LINK_PATTERN = /@([^\s@<>]+)/g; // Matches @linkName
const TAG_PATTERN = /<([^\s<>/]+)>([\s\S]*?)<\/\1>/g; // Matches <tagName>content</tagName>

/**
 * Parse template markup string to tiptap-compatible JSON content
 *
 * @param text - Template markup string
 * @param links - Dictionary of link names to URLs
 * @returns Tiptap JSONContent
 */
export function parseTemplateToTiptap(
  text: string,
  links: Record<string, string> = {},
  countdowns: Record<string, unknown> = {},
  flowVariableRefs: Set<string> = new Set()
): JSONContent {
  if (!text) {
    return {
      type: 'doc',
      content: [{ type: 'paragraph' }],
    };
  }

  let lastIndex = 0;

  // Build a list of all matches with their positions
  interface ParsedSegment {
    type: 'text' | 'link' | 'tag' | 'countdown' | 'flowVariable';
    start: number;
    end: number;
    content?: string;
    tagName?: string;
    linkId?: string;
    linkUrl?: string;
    countdownId?: string;
    flowVariableRef?: string;
  }

  const segments: ParsedSegment[] = [];

  // Find all @mention patterns — disambiguate countdown vs link
  let match: RegExpExecArray | null;
  const linkRegex = new RegExp(LINK_PATTERN.source, 'g');
  while ((match = linkRegex.exec(text)) !== null) {
    const mentionName = match[1];
    if (mentionName in countdowns) {
      // Countdown takes priority
      segments.push({
        type: 'countdown',
        start: match.index,
        end: match.index + match[0].length,
        countdownId: mentionName,
      });
    } else if (flowVariableRefs.has(mentionName)) {
      // Flow variable (V2)
      segments.push({
        type: 'flowVariable',
        start: match.index,
        end: match.index + match[0].length,
        flowVariableRef: mentionName,
      });
    } else {
      segments.push({
        type: 'link',
        start: match.index,
        end: match.index + match[0].length,
        linkId: mentionName,
        linkUrl: links[mentionName] || '',
      });
    }
  }

  // Find all <tag>content</tag> patterns
  const tagRegex = new RegExp(TAG_PATTERN.source, 'g');
  while ((match = tagRegex.exec(text)) !== null) {
    const tagName = match[1];
    const tagContent = match[2];
    segments.push({
      type: 'tag',
      start: match.index,
      end: match.index + match[0].length,
      tagName,
      content: tagContent,
    });
  }

  // Sort segments by start position
  segments.sort((a, b) => a.start - b.start);

  // Filter out overlapping segments (links inside tags should be handled separately)
  const filteredSegments: ParsedSegment[] = [];
  for (const segment of segments) {
    // Check if this segment overlaps with any existing segment
    const overlaps = filteredSegments.some(
      (existing) =>
        (segment.start >= existing.start && segment.start < existing.end) ||
        (segment.end > existing.start && segment.end <= existing.end)
    );
    if (!overlaps) {
      filteredSegments.push(segment);
    }
  }

  // Build content array
  const paragraphContent: JSONContent[] = [];

  for (const segment of filteredSegments) {
    // Add any text before this segment
    if (segment.start > lastIndex) {
      const textBefore = text.slice(lastIndex, segment.start);
      if (textBefore) {
        paragraphContent.push({ type: 'text', text: textBefore });
      }
    }

    if (segment.type === 'countdown') {
      // Add countdown mention node
      paragraphContent.push({
        type: 'countdownMention',
        attrs: {
          id: segment.countdownId,
        },
      });
    } else if (segment.type === 'flowVariable') {
      // Add flow variable mention node
      paragraphContent.push({
        type: 'flowVariableMention',
        attrs: {
          ref: segment.flowVariableRef,
        },
      });
    } else if (segment.type === 'link') {
      // Add link mention node
      paragraphContent.push({
        type: 'linkMention',
        attrs: {
          id: segment.linkId,
          url: segment.linkUrl,
        },
      });
    } else if (segment.type === 'tag') {
      // Check if tag wraps a single @mention: <tag>@mentionName</tag>
      const innerMentionMatch = segment.content?.match(/^@([^\s@<>]+)$/);
      if (innerMentionMatch) {
        const mentionName = innerMentionMatch[1];
        if (mentionName in countdowns) {
          paragraphContent.push({
            type: 'countdownMention',
            attrs: { id: mentionName, labelTag: segment.tagName },
          });
        } else if (flowVariableRefs.has(mentionName)) {
          paragraphContent.push({
            type: 'flowVariableMention',
            attrs: { ref: mentionName, labelTag: segment.tagName },
          });
        } else {
          paragraphContent.push({
            type: 'linkMention',
            attrs: { id: mentionName, url: links[mentionName] || '', labelTag: segment.tagName },
          });
        }
      } else if (segment.content) {
        // Regular label: add text with tag mark
        paragraphContent.push({
          type: 'text',
          text: segment.content,
          marks: [
            {
              type: 'templateTag',
              attrs: { tagName: segment.tagName },
            },
          ],
        });
      }
    }

    lastIndex = segment.end;
  }

  // Add any remaining text after the last segment
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    if (remainingText) {
      paragraphContent.push({ type: 'text', text: remainingText });
    }
  }

  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: paragraphContent.length > 0 ? paragraphContent : undefined,
      },
    ],
  };
}

/**
 * Serialize tiptap editor content back to template markup string
 *
 * @param editor - Tiptap editor instance
 * @returns Template markup string
 */
export function serializeTiptapToTemplate(editor: Editor): string {
  const json = editor.getJSON();
  return serializeJSONToTemplate(json);
}

/**
 * Serialize tiptap JSON content to template markup string
 *
 * @param json - Tiptap JSONContent
 * @returns Template markup string
 */
export function serializeJSONToTemplate(json: JSONContent): string {
  if (!json.content) {
    return '';
  }

  const parts: string[] = [];

  for (const node of json.content) {
    if (node.type === 'paragraph') {
      parts.push(serializeParagraph(node));
    } else if (node.type === 'text') {
      parts.push(serializeTextNode(node));
    }
  }

  return parts.join('\n');
}

/**
 * Serialize a paragraph node to template markup
 */
function serializeParagraph(paragraph: JSONContent): string {
  if (!paragraph.content) {
    return '';
  }

  const parts: string[] = [];

  for (const node of paragraph.content) {
    if (node.type === 'text') {
      parts.push(serializeTextNode(node));
    } else if (node.type === 'flowVariableMention') {
      // Serialize flow variable to @ref format, optionally wrapped in <labelTag>
      const ref = node.attrs?.ref || '';
      const labelTag = node.attrs?.labelTag;
      if (labelTag) {
        parts.push(`<${labelTag}>@${ref}</${labelTag}>`);
      } else {
        parts.push(`@${ref}`);
      }
    } else if (node.type === 'countdownMention' || node.type === 'linkMention') {
      // Serialize mention to @name format, optionally wrapped in <labelTag>
      const id = node.attrs?.id || '';
      const labelTag = node.attrs?.labelTag;
      const mentionText = `@${id}`;
      parts.push(labelTag ? `<${labelTag}>${mentionText}</${labelTag}>` : mentionText);
    }
  }

  return parts.join('');
}

/**
 * Serialize a text node, handling marks
 */
function serializeTextNode(textNode: JSONContent): string {
  const text = textNode.text || '';

  if (!textNode.marks || textNode.marks.length === 0) {
    return text;
  }

  // Find templateTag mark
  const tagMark = textNode.marks.find((mark) => mark.type === 'templateTag');

  if (tagMark && tagMark.attrs?.tagName) {
    // Wrap text in tag format
    return `<${tagMark.attrs.tagName}>${text}</${tagMark.attrs.tagName}>`;
  }

  return text;
}

/**
 * Extract all link mentions from template text
 *
 * @param text - Template markup string
 * @returns Array of link names found in the text
 */
export function extractLinkMentions(text: string): string[] {
  const mentions: string[] = [];
  const regex = new RegExp(LINK_PATTERN.source, 'g');
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    mentions.push(match[1]);
  }

  return [...new Set(mentions)]; // Remove duplicates
}

/**
 * Extract all tag names from template text
 *
 * @param text - Template markup string
 * @returns Array of tag names found in the text
 */
export function extractTagNames(text: string): string[] {
  const tags: string[] = [];
  const regex = new RegExp(TAG_PATTERN.source, 'g');
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    tags.push(match[1]);
  }

  return [...new Set(tags)]; // Remove duplicates
}

/**
 * Validate that all links and tags in the text have corresponding definitions
 *
 * @param text - Template markup string
 * @param links - Available link definitions
 * @param labels - Available label/tag definitions
 * @returns Object with missing links and tags arrays
 */
export function validateTemplateReferences(
  text: string,
  links: Record<string, string>,
  labels: Record<string, unknown>
): { missingLinks: string[]; missingTags: string[] } {
  const usedLinks = extractLinkMentions(text);
  const usedTags = extractTagNames(text);

  const missingLinks = usedLinks.filter((link) => !(link in links));
  const missingTags = usedTags.filter((tag) => !(tag in labels));

  return { missingLinks, missingTags };
}
